import posthog from "posthog-js";
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  SITE_POSTHOG_API_HOST,
  SITE_POSTHOG_PROJECT_TOKEN,
  START_JOURNEY_URL,
  shouldOpenSiteLinkInNewTab
} from "./siteConfig";

type DynamicLinkKey =
  | "app-store"
  | "play-store"
  | "billing"
  | "contact"
  | "funnel"
  | "support-email";

const trimEnv = (value: string | undefined): string => value?.trim() ?? "";

const supportEmail = trimEnv(import.meta.env.VITE_SUPPORT_EMAIL) || "support@sehaj.app";
const contactUrl = trimEnv(import.meta.env.VITE_CONTACT_URL) || "https://tally.so/r/J9YOE4";
const links: Record<DynamicLinkKey, string> = {
  "app-store":
    trimEnv(import.meta.env.VITE_APP_STORE_URL) ||
    APP_STORE_URL,
  "play-store": trimEnv(import.meta.env.VITE_PLAY_STORE_URL) || GOOGLE_PLAY_URL,
  billing: "/account/",
  contact: contactUrl,
  funnel: START_JOURNEY_URL,
  "support-email": `mailto:${supportEmail}`
};

type SiteAnalyticsWindow = Window & {
  __SEHAJ_SITE_PAGEVIEW_CAPTURED__?: boolean;
};

const isLocalPreview = ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);

const initializeSiteAnalytics = (): void => {
  if (isLocalPreview) return;
  if (!(posthog as { __loaded?: boolean }).__loaded) {
    posthog.init(SITE_POSTHOG_PROJECT_TOKEN, {
      api_host: SITE_POSTHOG_API_HOST,
      defaults: "2026-01-30",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: true,
      cross_subdomain_cookie: true,
      persistence: "localStorage+cookie",
      person_profiles: "identified_only"
    });
  }

  posthog.register({ site_surface: "public_website" });

  const analyticsWindow = window as SiteAnalyticsWindow;
  if (!analyticsWindow.__SEHAJ_SITE_PAGEVIEW_CAPTURED__) {
    analyticsWindow.__SEHAJ_SITE_PAGEVIEW_CAPTURED__ = true;
    posthog.capture("$pageview", {
      $current_url: window.location.href,
      site_page_path: window.location.pathname,
      site_surface: "public_website"
    });
  }
};

const applyDynamicLinks = (): void => {
  for (const element of document.querySelectorAll<HTMLAnchorElement>("[data-link]")) {
    const key = element.dataset.link as DynamicLinkKey | undefined;
    if (!key) continue;

    const url = links[key];
    if (!url) {
      element.setAttribute("aria-disabled", "true");
      element.dataset.pending = "true";
      continue;
    }

    element.href = url;
    element.dataset.pending = "false";

    if (key === "funnel") {
      element.addEventListener("click", () => {
        if (isLocalPreview) return;
        posthog.capture(
          "website_start_journey_clicked",
          {
            cta_text: element.textContent?.trim() || "Start your journey",
            destination: START_JOURNEY_URL,
            site_page_path: window.location.pathname,
            site_surface: "public_website"
          },
          { transport: "sendBeacon", send_instantly: true }
        );
      });
    }

    if (key === "app-store" || key === "play-store") {
      element.addEventListener("click", () => {
        if (isLocalPreview) return;
        posthog.capture(
          "website_store_clicked",
          {
            store: key === "app-store" ? "apple_app_store" : "google_play",
            destination: url,
            site_page_path: window.location.pathname,
            site_surface: "public_website"
          },
          { transport: "sendBeacon", send_instantly: true }
        );
      });
    }

    if (shouldOpenSiteLinkInNewTab(key, url)) {
      element.target = "_blank";
      element.rel = "noopener noreferrer";
    } else {
      element.removeAttribute("target");
      element.removeAttribute("rel");
    }
  }
};

initializeSiteAnalytics();
applyDynamicLinks();
